import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import ActionButton from "../../components/ui/ActionButton";
import { Input } from "../../components/ui/input";
import PaginatedSelect from "../../components/ui/PaginatedSelect";
import QRCodePreview from "../../components/ui/QRCodePreview";

const getInitialFormData = (fields, source = null) =>
  fields.reduce((acc, field) => {
    const fallback = field.type === "checkbox" ? false : field.defaultValue ?? "";
    const value = source?.[field.name] ?? fallback;

    acc[field.name] =
      field.multiple
        ? Array.isArray(value) ? value.map(String) : []
        : field.type === "checkbox"
        ? Boolean(value)
        : value === null
        ? ""
        : String(value);
    return acc;
  }, {});

const parseFieldValue = (field, value) => {
  if (field.type === "checkbox") return Boolean(value);
  if (field.type === "number" || field.valueType === "number") {
    return value === "" ? null : Number(value);
  }
  return typeof value === "string" ? value.trim() : value;
};

const buildPayload = (fields, formData) =>
  fields.reduce((acc, field) => {
    acc[field.name] = parseFieldValue(field, formData[field.name]);
    return acc;
  }, {});

const resolveFieldSetting = (setting, formData, editData, field) =>
  typeof setting === "function" ? setting(formData, editData, field) : setting;

const MasterForm = ({
  entityName,
  plantId,
  editData,
  fields,
  isOpen,
  isSaving,
  onClose,
  onCreate,
  onRequestUpdate,
  qrDisplayValue,
  qrValue,
  transformPayload,
  extraContent,
  submitDisabled = false,
  saveError,
}) => {
  const [formData, setFormData] = useState(() => getInitialFormData(fields));
  const [errorMessage, setErrorMessage] = useState("");

  const formSession = useRef(null);

  useEffect(() => {
    const previous = formSession.current;
    // Loading options changes field definitions without starting a new form.
    if (
      previous &&
      previous.editData === editData &&
      previous.isOpen === isOpen &&
      previous.plantId === plantId
    ) return;
    formSession.current = { editData, isOpen, plantId };
    setFormData({
      ...getInitialFormData(fields, editData),
      ...(plantId ? { plant_id: editData?.plant_id || plantId } : {}),
    });
    setErrorMessage("");
  }, [editData, fields, isOpen, plantId]);

  const handleFieldValueChange = (field, value) => {
    field.onChange?.(value);
    setFormData((current) => {
      const next = {
        ...current,
        [field.name]: value,
      };

      (field.resetFields || []).forEach((fieldName) => {
        next[fieldName] = "";
      });

      return next;
    });
    setErrorMessage("");
  };

  const handleFieldChange = (field, event) => {
    const value =
      field.type === "checkbox" ? event.target.checked : event.target.value;

    handleFieldValueChange(field, value);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (submitDisabled || isSaving) return;

    const missingField = fields.find(
      (field) =>
        field.required &&
        field.type !== "checkbox" &&
        !String(formData[field.name] ?? "").trim()
    );

    if (missingField) {
      setErrorMessage(`${missingField.label} is required.`);
      return;
    }

    const payload = buildPayload(fields, formData);
    const nextPayload = transformPayload
      ? transformPayload(payload, editData)
      : payload;

    if (editData) {
      onRequestUpdate(nextPayload);
      return;
    }

    onCreate(nextPayload);
  };

  const action = editData ? "update" : "submit";

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !isSaving && onClose()}>
      <DialogContent className={extraContent ? "sm:max-w-[760px]" : "sm:max-w-[620px]"}>
        <DialogHeader>
          <div className="flex items-center justify-between gap-3">
            <DialogTitle>
              {editData ? `Edit ${entityName}` : `Add ${entityName}`}
            </DialogTitle>
            <ActionButton action="cancel" iconOnly variant="ghost" disabled={isSaving} onClick={onClose} />
          </div>
        </DialogHeader>

        <form className="mt-2 space-y-5" onSubmit={handleSubmit}>
          {editData && qrValue && (
            <div className="rounded-lg bg-slate-50 px-4 py-3">
              <QRCodePreview
                value={qrValue}
                displayValue={qrDisplayValue}
                label={`${entityName} ${qrValue}`}
                size={112}
                showValue
                className="mx-auto"
              />
            </div>
          )}

          <fieldset disabled={isSaving} className="grid gap-4 sm:grid-cols-2">
            {fields.map((field) => {
              const value = formData[field.name];
              const placeholder = resolveFieldSetting(
                field.placeholder,
                formData,
                editData,
                field
              );
              const isDisabled =
                Boolean(resolveFieldSetting(field.disabled, formData, editData, field)) ||
                (field.disabledOnEdit && Boolean(editData));

              if (field.type === "checkbox") {
                return (
                  <label
                    key={field.name}
                    className="flex min-h-10 items-center gap-3 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700"
                  >
                    <input
                      type="checkbox"
                      checked={Boolean(value)}
                      onChange={(event) => handleFieldChange(field, event)}
                      disabled={isDisabled}
                      className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                    />
                    <span>{field.label}</span>
                  </label>
                );
              }

              if (field.type === "select") {
                const fieldOptions =
                  resolveFieldSetting(field.options, formData, editData, field) || [];

                return (
                  <label
                    key={field.name}
                    className={field.fullWidth ? "sm:col-span-2" : ""}
                  >
                    <span className="mb-1.5 block text-sm font-medium text-slate-700">
                      {field.label}
                    </span>
                    <PaginatedSelect
                      multiple={field.multiple}
                      value={value}
                      onChange={(nextValue) =>
                        handleFieldValueChange(field, nextValue)
                      }
                      options={fieldOptions}
                      disabled={isDisabled}
                      placeholder={placeholder || `Select ${field.label}`}
                    />
                  </label>
                );
              }

              return (
                <label
                  key={field.name}
                  className={field.fullWidth ? "sm:col-span-2" : ""}
                >
                  <span className="mb-1.5 block text-sm font-medium text-slate-700">
                    {field.label}
                  </span>
                  <Input
                    type={field.type || "text"}
                    value={value}
                    onChange={(event) => handleFieldChange(field, event)}
                    placeholder={placeholder}
                    required={field.required}
                    disabled={isDisabled}
                    min={field.min}
                    step={field.step}
                  />
                </label>
              );
            })}
          </fieldset>

          <fieldset disabled={isSaving}>{typeof extraContent === "function" ? extraContent(isSaving) : extraContent}</fieldset>
          {saveError && <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">{saveError}</p>}

          {errorMessage && (
            <p className="rounded-md bg-red-50 px-3 py-2 text-sm font-medium text-red-600">
              {errorMessage}
            </p>
          )}

          <div className="flex justify-end gap-3 border-t border-slate-100 pt-4">
            <ActionButton action="cancel" variant="outline" disabled={isSaving} onClick={onClose} />
            <ActionButton
              action={action}
              type="submit"
              isLoading={isSaving}
              disabled={submitDisabled}
              label={editData ? `Update ${entityName}` : `Create ${entityName}`}
            />
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default MasterForm;
